import {useCallback} from 'react';
import {OCRDetectedData} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '../../../analytics';
import {useOCR} from '../../useOCR';
import {OCRFlowActions, OCRFlowContext, OCRFlowHandlers} from './types';
import {
  buildOCRImageDetails,
  getDocumentCompletionEvent,
  resolveOCRFailure,
  shouldPersistOCRImage,
} from './helpers';

type UseOCRFlowProps = {
  context: OCRFlowContext;
  actions: OCRFlowActions;
  handlers: OCRFlowHandlers;
};

function useOCRFlow({context, actions, handlers}: UseOCRFlowProps) {
  const {
    docType,
    onlyScanning,
    isBiomatchAndPassport,
    documentType,
    frontSideImage,
    countryCode,
    reservationId,
  } = context;
  const {saveOCRData, saveOCRImageDetails, finishFlow} = actions;
  const {onError, onBefore, onRetryLimitReached, onFailed} = handlers;

  const handleOCRSuccess = useCallback(
    async (image: string, data?: OCRDetectedData) => {
      if (data) {
        await saveOCRData(data);
      }

      if (
        docType &&
        shouldPersistOCRImage({isBiomatchAndPassport, onlyScanning, docType})
      ) {
        const docTypeImageDetails = buildOCRImageDetails(image, docType, data);
        await saveOCRImageDetails(docType, docTypeImageDetails);

        const completionEvent = getDocumentCompletionEvent(docType);
        if (completionEvent) {
          Analytics.track(completionEvent);
        }
      }

      if (onlyScanning) {
        Analytics.track(ANALYTIC_EVENTS.ivFinishingDocumentMRZScan);
      }

      finishFlow();
    },
    [
      docType,
      finishFlow,
      isBiomatchAndPassport,
      onlyScanning,
      saveOCRData,
      saveOCRImageDetails,
    ],
  );

  const {sendAndVerifyImageForOCR} = useOCR({
    onRetriesFinish: onRetryLimitReached,
    onDetectionFailed: (errorMessage, data) => {
      const resolution = resolveOCRFailure({
        documentTypeWithoutMrz: documentType?.without_mrz,
        documentTypeBothSidesMrz: documentType?.both_sides_mrz,
        hasFrontSideImage: Boolean(frontSideImage.image),
        docType,
      });

      if (resolution.type === 'success_without_mrz') {
        void handleOCRSuccess(data.image, {...data, mrz_detected: true});
        return;
      }

      if (resolution.type === 'retry_front_side_as_ocr') {
        if (!documentType?.value) {
          onFailed(errorMessage);
          return;
        }

        void sendAndVerifyImageForOCR({
          documentType: documentType.value,
          nationality: documentType.value,
          image: frontSideImage.image || '',
          source: data.source || '',
          countryCode: countryCode || '',
          reservationId: reservationId || '',
        });
        return;
      }

      onFailed(errorMessage);
    },
    onFinish: handleOCRSuccess,
    onError,
    onBefore,
  });

  return {sendAndVerifyImageForOCR};
}

export {useOCRFlow};
